/**
 * Scrubber - a draggable progress bar with a thumb.
 *
 * Supports tap-to-seek and press-and-drag. While dragging it shows the dragged
 * position locally (so it feels responsive even before the video catches up)
 * and reports the fraction via `onSeek(fraction, committed)`:
 *   - committed=false during the drag (preview seeks)
 *   - committed=true  on release
 * All pointer events are stopped so the slide's tap/swipe handlers don't fire.
 */
import { useRef, useState } from 'preact/hooks';

export function Scrubber({ progress = 0, onSeek, onActiveChange }) {
  const trackRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [dragFrac, setDragFrac] = useState(0);

  const fracFromEvent = (e) => {
    const rect = trackRef.current.getBoundingClientRect();
    return Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
  };

  const onPointerDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    trackRef.current.setPointerCapture(e.pointerId);
    const f = fracFromEvent(e);
    setDragging(true);
    setDragFrac(f);
    onActiveChange?.(true);
    onSeek?.(f, false);
  };

  const onPointerMove = (e) => {
    if (!dragging) return;
    e.stopPropagation();
    const f = fracFromEvent(e);
    setDragFrac(f);
    onSeek?.(f, false);
  };

  const onPointerUp = (e) => {
    if (!dragging) return;
    e.stopPropagation();
    try {
      trackRef.current.releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
    const f = fracFromEvent(e);
    setDragging(false);
    onActiveChange?.(false);
    onSeek?.(f, true);
  };

  const pct = dragging ? dragFrac * 100 : progress;

  return (
    <div
      class={`scrubber ${dragging ? 'scrubber--dragging' : ''}`}
      ref={trackRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClick={(e) => e.stopPropagation()}
    >
      <div class="scrubber__track">
        <div class="scrubber__fill" style={{ width: `${pct}%` }} />
        <div class="scrubber__thumb" style={{ left: `${pct}%` }} />
      </div>
    </div>
  );
}
